import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import * as mammoth from 'mammoth';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import RichTextEditor from '@/components/feature/RichTextEditor';
import type { BlogPost } from '@/mocks/blog';

const navTabs = [
  { label: 'Home', href: '/' },
  { label: 'Marketplace', href: '/#marketplace' },
  { label: 'For Homes', href: '/#homes' },
  { label: 'For Businesses', href: '/#businesses' },
  { label: 'For Partners', href: '/#partners' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/#contact' },
];

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 80);
}

export default function BlogEditPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getPostBySlug, createPost, updatePost, savePreview, posts } = useBlogPosts();

  const isEditing = Boolean(slug);
  const existingPost = isEditing ? getPostBySlug(slug || '') : undefined;

  const [title, setTitle] = useState(existingPost?.title || '');
  const [excerpt, setExcerpt] = useState(existingPost?.excerpt || '');
  const [content, setContent] = useState(existingPost?.content || '');
  const [coverImage, setCoverImage] = useState(existingPost?.coverImage || '');
  const [author, setAuthor] = useState(existingPost?.author || 'AK Energy Editorial Team');
  const [tags, setTags] = useState(existingPost?.tags?.join(', ') || '');
  const [status, setStatus] = useState<'draft' | 'published'>(existingPost?.status || 'draft');
  const [seoTitle, setSeoTitle] = useState(existingPost?.seoTitle || '');
  const [seoDescription, setSeoDescription] = useState(existingPost?.seoDescription || '');
  const [coverPreview, setCoverPreview] = useState<string | null>(existingPost?.coverImage || null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [wordImportLoading, setWordImportLoading] = useState(false);

  const wordInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = isEditing ? 'Edit Blog Post | AK Energy Admin' : 'New Blog Post | AK Energy Admin';
  }, [isEditing]);

  const handleContentChange = useCallback((html: string) => {
    setContent(html);
  }, []);

  const handleWordUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.endsWith('.docx')) {
      setSaveMessage('Please upload a .docx Word document.');
      setTimeout(() => setSaveMessage(''), 4000);
      return;
    }

    setWordImportLoading(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      const html = result.value;

      // Clean up mammoth output for better compatibility
      const cleanHtml = html
        .replace(/<p><\/p>/g, '')
        .replace(/ style="[^"]*"/g, '')
        .replace(/ class="[^"]*"/g, '');

      setContent(cleanHtml);
      setSaveMessage('Word document imported successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch {
      setSaveMessage('Failed to import Word document. Please try again.');
      setTimeout(() => setSaveMessage(''), 4000);
    } finally {
      setWordImportLoading(false);
      if (wordInputRef.current) wordInputRef.current.value = '';
    }
  }, []);

  const handleCoverImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSaveMessage('Please upload an image file (JPG, PNG, WebP).');
      setTimeout(() => setSaveMessage(''), 4000);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setCoverPreview(result);
      setCoverImage(result);
    };
    reader.readAsDataURL(file);

    if (coverInputRef.current) coverInputRef.current.value = '';
  }, []);

  const handleSave = useCallback(() => {
    if (!title.trim()) {
      setSaveMessage('Please enter a title.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setIsSaving(true);

    const postSlug = existingPost?.slug || generateSlug(title);
    const tagArray = tags.split(',').map((t) => t.trim()).filter(Boolean);

    const postData: Omit<BlogPost, 'id' | 'publishedAt'> = {
      slug: postSlug,
      title: title.trim(),
      excerpt: excerpt.trim() || title.trim(),
      content,
      coverImage: coverImage || 'https://readdy.ai/api/search-image?query=Abstract%20renewable%20energy%20concept%20with%20solar%20panels%20wind%20turbines%20and%20green%20energy%20symbols%2C%20clean%20minimal%20illustration%20style%2C%20soft%20gradient%20background%2C%20professional%20editorial%20design&width=1200&height=630&seq=blog-default&orientation=landscape',
      author: author.trim() || 'AK Energy Editorial Team',
      updatedAt: new Date().toISOString().split('T')[0],
      status,
      tags: tagArray,
      seoTitle: seoTitle.trim() || title.trim(),
      seoDescription: seoDescription.trim() || excerpt.trim(),
    };

    if (isEditing && existingPost) {
      updatePost(existingPost.id, postData);
      setSaveMessage('Post updated successfully!');
    } else {
      createPost(postData);
      setSaveMessage('Post created successfully!');
    }

    setIsSaving(false);
    setTimeout(() => setSaveMessage(''), 3000);
  }, [title, excerpt, content, coverImage, author, tags, status, seoTitle, seoDescription, isEditing, existingPost, createPost, updatePost]);

  const handlePreview = useCallback(() => {
    if (!title.trim()) {
      setSaveMessage('Please enter a title before previewing.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    const previewSlug = existingPost?.slug || generateSlug(title);
    const previewPost: BlogPost = {
      id: existingPost?.id || 'preview',
      slug: previewSlug,
      title: title.trim(),
      excerpt: excerpt.trim() || title.trim(),
      content,
      coverImage: coverImage || 'https://readdy.ai/api/search-image?query=Abstract%20renewable%20energy%20concept%20with%20solar%20panels%20wind%20turbines%20and%20green%20energy%20symbols%2C%20clean%20minimal%20illustration%20style%2C%20soft%20gradient%20background%2C%20professional%20editorial%20design&width=1200&height=630&seq=blog-default&orientation=landscape',
      author: author.trim() || 'AK Energy Editorial Team',
      publishedAt: existingPost?.publishedAt || new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      status: 'published',
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      seoTitle: seoTitle.trim() || title.trim(),
      seoDescription: seoDescription.trim() || excerpt.trim(),
    };

    savePreview(previewPost);

    // Open preview in new tab
    const basePath = __BASE_PATH__ ? `/${__BASE_PATH__}` : '';
    const previewUrl = `${window.location.origin}${basePath}/blog/preview`;
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  }, [title, excerpt, content, coverImage, author, tags, status, seoTitle, seoDescription, existingPost, savePreview]);

  const handleImageInsert = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const imgHtml = `<p><img src="${result}" alt="${file.name}" style="max-width:100%;height:auto;border-radius:8px;" /></p>`;
      setContent((prev) => prev + imgHtml);
    };
    reader.readAsDataURL(file);
    if (imageInputRef.current) imageInputRef.current.value = '';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top Navigation Tabs */}
      <div className="sticky top-0 z-50 bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png"
                alt="AK Energy"
                className="h-7 w-auto"
              />
            </Link>
            <div className="flex items-center gap-0 overflow-x-auto scrollbar-hide ml-4">
              {navTabs.map((tab) => (
                <Link
                  key={tab.label}
                  to={tab.href}
                  className="px-3 py-1.5 text-xs font-medium text-foreground-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap"
                >
                  {tab.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {isEditing ? 'Edit Blog Post' : 'New Blog Post'}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              {isEditing ? 'Update your existing article below.' : 'Create a new article for the AK Energy blog.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/blog/admin"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-background-200 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" /> Back
            </Link>
          </div>
        </div>

        {/* Save Status */}
        {saveMessage && (
          <div className={`mb-6 px-4 py-3 rounded-xl text-sm font-medium ${
            saveMessage.includes('success') || saveMessage.includes('imported')
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {saveMessage}
          </div>
        )}

        {/* Import Actions Bar */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <input
            ref={wordInputRef}
            type="file"
            accept=".docx"
            onChange={handleWordUpload}
            className="hidden"
          />
          <button
            onClick={() => wordInputRef.current?.click()}
            disabled={wordImportLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-background-200 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className={`${wordImportLoading ? 'ri-loader-4-line animate-spin' : 'ri-file-word-line'}`} />
            {wordImportLoading ? 'Importing...' : 'Import Word Doc'}
          </button>

          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageInsert}
            className="hidden"
          />
          <button
            onClick={() => imageInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-background-200 rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer"
          >
            <i className="ri-image-add-line" />
            Insert Image
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Editor Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-2">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter blog post title..."
                className="w-full px-4 py-3 text-base font-medium bg-white border border-background-200 rounded-xl text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-2">Excerpt</label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Short summary for cards and previews..."
                rows={3}
                maxLength={300}
                className="w-full px-4 py-3 text-sm bg-white border border-background-200 rounded-xl text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
              />
              <p className="text-xs text-foreground-400 mt-1 text-right">{excerpt.length}/300</p>
            </div>

            {/* Content Editor */}
            <div>
              <label className="block text-sm font-semibold text-foreground-700 mb-2">Content</label>
              <RichTextEditor
                initialValue={content}
                onChange={handleContentChange}
                placeholder="Write your blog post content here... Use the toolbar to format text, add headings, lists, and links."
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publish Actions */}
            <div className="bg-white border border-background-200 rounded-xl p-5">
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Publish</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Status</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setStatus('draft')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                        status === 'draft'
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : 'bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-100'
                      }`}
                    >
                      <i className="ri-draft-line mr-1" /> Draft
                    </button>
                    <button
                      onClick={() => setStatus('published')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                        status === 'published'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-100'
                      }`}
                    >
                      <i className="ri-check-line mr-1" /> Published
                    </button>
                  </div>
                </div>

                <button
                  onClick={handlePreview}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-background-200 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <i className="ri-eye-line" />
                  Preview
                </button>

                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  <i className={`${isSaving ? 'ri-loader-4-line animate-spin' : 'ri-save-line'}`} />
                  {isSaving ? 'Saving...' : isEditing ? 'Update Post' : 'Save Post'}
                </button>
              </div>
            </div>

            {/* Cover Image */}
            <div className="bg-white border border-background-200 rounded-xl p-5">
              <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Cover Image</h3>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverImageUpload}
                className="hidden"
              />

              {coverPreview ? (
                <div className="relative rounded-lg overflow-hidden mb-3">
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="w-full h-40 object-cover"
                  />
                  <button
                    onClick={() => { setCoverPreview(null); setCoverImage(''); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center text-foreground-600 hover:text-red-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => coverInputRef.current?.click()}
                  className="w-full py-8 border-2 border-dashed border-background-300 rounded-lg flex flex-col items-center gap-2 text-foreground-400 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-image-add-line text-2xl" />
                  <span className="text-xs font-medium">Upload cover image</span>
                </button>
              )}
            </div>

            {/* Meta Fields */}
            <div className="bg-white border border-background-200 rounded-xl p-5 space-y-4">
              <h3 className="font-heading text-sm font-bold text-foreground-950">Post Settings</h3>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Author</label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Author name..."
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Tags (comma separated)</label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="Solar, Battery, Guide..."
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">SEO Title</label>
                <input
                  type="text"
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  placeholder="Custom SEO title..."
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">SEO Description</label>
                <textarea
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  placeholder="Meta description for search engines..."
                  rows={3}
                  maxLength={160}
                  className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all resize-none"
                />
                <p className="text-xs text-foreground-400 mt-1 text-right">{seoDescription.length}/160</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}